import type { ReactNode } from 'react';
import { Fragment, memo } from 'react';
import styled, { css } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import Button, { type IButtonProps } from '../deprecated-button/button';
import type { RIconComponent } from '../remix-icon';
import TruncatedText from '../truncated-text/truncated-text';
import PageHeaderBanner from './page-header-banner';

export interface PageHeaderButtonProps extends IButtonProps {
  hasRightSideDivider?: boolean;
  showButton?: boolean;
  'data-testid'?: string;
}

export interface PageHeaderProps {
  title?: string;
  buttons?: PageHeaderButtonProps[];
  bannerText?: ReactNode;
  bannerIcon?: RIconComponent;
  className?: string;
}

const PageHeader = ({
  title,
  buttons,
  bannerText,
  bannerIcon,
  className,
}: PageHeaderProps) => {
  return (
    <>
      {bannerText && bannerIcon && (
        <PageHeaderBanner bannerIcon={bannerIcon} bannerText={bannerText} />
      )}
      <Wrapper data-testid="page-header-wrapper" className={className}>
        <TitleWrapper>
          <TruncatedText variant={TypographyVariant.h3}>{title}</TruncatedText>
        </TitleWrapper>
        {buttons && (
          <ButtonContainer>
            {buttons.map((button, index) => {
              const {
                hasRightSideDivider,
                showButton = true,
                ...props
              } = button;
              return (
                <Fragment key={index}>
                  {showButton && <Button width="auto" {...props} />}
                  {hasRightSideDivider && showButton && (
                    <ButtonVerticalDivider data-testid="button-vertical-divider" />
                  )}
                </Fragment>
              );
            })}
          </ButtonContainer>
        )}
      </Wrapper>
    </>
  );
};

export default memo(PageHeader);

const ButtonVerticalDivider = styled.div`
  ${({ theme }) => css`
    width: 1px;
    height: auto;
    background-color: ${theme.palette.neutral[60]};
    margin: ${theme.spacing(1, 0)};
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    border-bottom: 1px solid ${theme.palette.neutral[60]};
    padding: ${theme.spacing(1.25, 5)};
    justify-content: space-between;
    align-items: center;
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(1)};
  `}
`;

const TitleWrapper = styled.div`
  max-width: 400px;
`;
